import React, { useState, useMemo } from 'react';
import { FamilyMember } from '../types';
import { User, ArrowRight, Share2, MapPin, Calendar, Crown, Heart, Baby, Users as UsersIcon, Plus, Zap } from 'lucide-react';

interface NetworkViewProps {
  members: Record<string, FamilyMember>;
  currentUser: FamilyMember;
  onAddConnection: () => void;
  onViewProfile: (id: string) => void;
}

const NetworkView: React.FC<NetworkViewProps> = ({ members, currentUser, onAddConnection, onViewProfile }) => {
  // All hooks must be called unconditionally at the top level
  const [focusId, setFocusId] = useState(currentUser?.id || '');
  const focusMember = focusId ? (members[focusId] || currentUser) : currentUser;

  // --- 1. CALCULATE ORBITS ---
  const connections = useMemo(() => {
    if (!currentUser || !members || Object.keys(members).length === 0) return [];
    if (!focusMember) return [];

    const list: { member: FamilyMember; relation: string; color: string; icon: any }[] = [];
    const addedIds = new Set<string>();

    // Parents
    (focusMember.parentIds || []).forEach(id => {
      if (members[id] && !addedIds.has(id)) {
        addedIds.add(id);
        list.push({ member: members[id], relation: 'Parent', color: 'bg-indigo-500', icon: Crown });
      }
    });

    // Spouse
    (focusMember.spouseIds || []).forEach(id => {
      if (members[id] && !addedIds.has(id)) {
        addedIds.add(id);
        list.push({ member: members[id], relation: 'Spouse', color: 'bg-pink-500', icon: Heart });
      }
    });

    // Children
    (focusMember.childrenIds || []).forEach(id => {
      if (members[id] && !addedIds.has(id)) {
        addedIds.add(id);
        list.push({ member: members[id], relation: 'Child', color: 'bg-emerald-500', icon: Baby });
      }
    });

    // Siblings (Share at least one parent) - CHECK ALL PARENTS
    const siblingIds = new Set<string>();
    (focusMember.parentIds || []).forEach(parentId => {
      const parent = members[parentId];
      if (parent) {
        (parent.childrenIds || []).forEach(childId => {
          if (childId !== focusMember.id) {
            siblingIds.add(childId);
          }
        });
      }
    });
    siblingIds.forEach(siblingId => {
      if (members[siblingId] && !addedIds.has(siblingId)) {
        addedIds.add(siblingId);
        list.push({ member: members[siblingId], relation: 'Sibling', color: 'bg-amber-500', icon: UsersIcon });
      }
    });

    // Grandparents (Parents of parents)
    (focusMember.parentIds || []).forEach(parentId => {
      const parent = members[parentId];
      if (parent) {
        (parent.parentIds || []).forEach(grandparentId => {
          if (members[grandparentId] && !addedIds.has(grandparentId)) {
            addedIds.add(grandparentId);
            list.push({ member: members[grandparentId], relation: 'Grandparent', color: 'bg-violet-500', icon: Crown });
          }
        });
      }
    });

    // Great-Grandparents (Parents of grandparents)
    (focusMember.parentIds || []).forEach(parentId => {
      const parent = members[parentId];
      if (parent) {
        (parent.parentIds || []).forEach(grandparentId => {
          const grandparent = members[grandparentId];
          if (grandparent) {
            (grandparent.parentIds || []).forEach(greatGrandparentId => {
              if (members[greatGrandparentId] && !addedIds.has(greatGrandparentId)) {
                addedIds.add(greatGrandparentId);
                list.push({ member: members[greatGrandparentId], relation: 'Great-Grandparent', color: 'bg-slate-500', icon: Crown });
              }
            });
          }
        });
      }
    });

    // Grandchildren (Children of children)
    (focusMember.childrenIds || []).forEach(childId => {
      const child = members[childId];
      if (child) {
        (child.childrenIds || []).forEach(grandchildId => {
          if (members[grandchildId] && !addedIds.has(grandchildId)) {
            addedIds.add(grandchildId);
            list.push({ member: members[grandchildId], relation: 'Grandchild', color: 'bg-emerald-600', icon: Baby });
          }
        });
      }
    });

    // Great-Grandchildren (Children of grandchildren)
    (focusMember.childrenIds || []).forEach(childId => {
      const child = members[childId];
      if (child) {
        (child.childrenIds || []).forEach(grandchildId => {
          const grandchild = members[grandchildId];
          if (grandchild) {
            (grandchild.childrenIds || []).forEach(greatGrandchildId => {
              if (members[greatGrandchildId] && !addedIds.has(greatGrandchildId)) {
                addedIds.add(greatGrandchildId);
                list.push({ member: members[greatGrandchildId], relation: 'Great-Grandchild', color: 'bg-emerald-700', icon: Baby });
              }
            });
          }
        });
      }
    });

    // Aunts/Uncles (Parents' siblings)
    (focusMember.parentIds || []).forEach(parentId => {
      const parent = members[parentId];
      if (parent) {
        // Parents' siblings
        (parent.parentIds || []).forEach(grandparentId => {
          const grandparent = members[grandparentId];
          if (grandparent) {
            (grandparent.childrenIds || []).forEach(auntUncleId => {
              if (auntUncleId !== parentId && members[auntUncleId] && !addedIds.has(auntUncleId)) {
                addedIds.add(auntUncleId);
                list.push({ member: members[auntUncleId], relation: 'Aunt/Uncle', color: 'bg-orange-500', icon: UsersIcon });
              }
            });
          }
        });
        // Parents' spouses (step-parents/in-laws)
        (parent.spouseIds || []).forEach(spouseId => {
          if (spouseId !== (focusMember.spouseIds?.[0]) && members[spouseId] && !addedIds.has(spouseId)) {
            addedIds.add(spouseId);
            list.push({ member: members[spouseId], relation: 'Step-Parent', color: 'bg-indigo-400', icon: Crown });
          }
        });
      }
    });

    // Cousins (Children of aunts/uncles)
    const auntUncleIds = new Set<string>();
    (focusMember.parentIds || []).forEach(parentId => {
      const parent = members[parentId];
      if (parent) {
        (parent.parentIds || []).forEach(grandparentId => {
          const grandparent = members[grandparentId];
          if (grandparent) {
            (grandparent.childrenIds || []).forEach(auntUncleId => {
              if (auntUncleId !== parentId) {
                auntUncleIds.add(auntUncleId);
              }
            });
          }
        });
      }
    });
    auntUncleIds.forEach(auntUncleId => {
      const auntUncle = members[auntUncleId];
      if (auntUncle) {
        (auntUncle.childrenIds || []).forEach(cousinId => {
          if (members[cousinId] && !addedIds.has(cousinId)) {
            addedIds.add(cousinId);
            list.push({ member: members[cousinId], relation: 'Cousin', color: 'bg-cyan-500', icon: UsersIcon });
          }
        });
      }
    });

    // In-laws from Spouse's family
    (focusMember.spouseIds || []).forEach(spouseId => {
      const spouse = members[spouseId];
      if (spouse) {
        // Spouse's parents (in-laws)
        (spouse.parentIds || []).forEach(parentInLawId => {
          if (members[parentInLawId] && !addedIds.has(parentInLawId)) {
            addedIds.add(parentInLawId);
            list.push({ member: members[parentInLawId], relation: 'In-Law Parent', color: 'bg-rose-500', icon: Crown });
          }
        });
        // Spouse's siblings (in-laws)
        const spouseSiblingIds = new Set<string>();
        (spouse.parentIds || []).forEach(parentId => {
          const parent = members[parentId];
          if (parent) {
            (parent.childrenIds || []).forEach(childId => {
              if (childId !== spouseId) {
                spouseSiblingIds.add(childId);
              }
            });
          }
        });
        spouseSiblingIds.forEach(siblingInLawId => {
          if (members[siblingInLawId] && !addedIds.has(siblingInLawId)) {
            addedIds.add(siblingInLawId);
            list.push({ member: members[siblingInLawId], relation: 'In-Law Sibling', color: 'bg-rose-400', icon: UsersIcon });
          }
        });
      }
    });

    // Children's spouses (in-laws)
    (focusMember.childrenIds || []).forEach(childId => {
      const child = members[childId];
      if (child) {
        (child.spouseIds || []).forEach(childSpouseId => {
          if (members[childSpouseId] && !addedIds.has(childSpouseId)) {
            addedIds.add(childSpouseId);
            list.push({ member: members[childSpouseId], relation: 'Child In-Law', color: 'bg-rose-600', icon: Heart });
          }
        });
      }
    });

    return list;
  }, [focusMember, members, currentUser]);

  // Safety check - if currentUser or members are missing, show loading state
  if (!currentUser || !members || Object.keys(members).length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full bg-indigo-100 mx-auto mb-4 flex items-center justify-center">
            <div className="w-8 h-8 rounded-full border-2 border-indigo-300 border-t-indigo-600 animate-spin"></div>
          </div>
          <p className="text-slate-600 font-bold">Loading circles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-100px)] w-full bg-slate-50 flex flex-col items-center justify-center relative overflow-hidden max-w-full">
      
      {/* Background Decor */}
      <div className="absolute inset-0 z-0 opacity-5">
         <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-slate-900 rounded-full"></div>
         <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] border border-slate-900 rounded-full"></div>
      </div>

      {/* --- HEADER --- */}
      <div className="absolute top-6 left-6 z-10">
        <h2 className="text-2xl font-black text-slate-900 flex items-center gap-2">
          <Share2 className="w-6 h-6 text-indigo-600" /> My Circles
        </h2>
        <p className="text-slate-500 text-sm">Interactive Relationship Explorer</p>
      </div>

      {/* --- INVITE BUTTON (NEW) --- */}
      <button 
        onClick={onAddConnection}
        className="absolute top-6 right-6 z-20 px-5 py-2.5 bg-white text-indigo-600 font-bold rounded-xl shadow-md border border-indigo-100 hover:bg-indigo-50 transition-all flex items-center gap-2"
      >
        <Plus className="w-4 h-4" /> Invite Family
      </button>

      {/* --- THE SOLAR SYSTEM --- */}
      <div className="relative w-full max-w-lg aspect-square flex items-center justify-center z-10">
        
        {/* 1. THE SUN (Focus Person) */}
        <div className="relative z-20 flex flex-col items-center animate-scale-in">
           <div className="w-32 h-32 rounded-full border-4 border-white shadow-2xl overflow-hidden relative bg-white">
              <img 
                src={focusMember.avatarUrl || `https://ui-avatars.com/api/?name=${focusMember.name}`} 
                className="w-full h-full object-cover"
              />
           </div>
           <div className="mt-4 text-center bg-white/90 backdrop-blur px-4 py-2 rounded-xl shadow-sm border border-slate-100">
              <h3 className="text-xl font-black text-slate-900">{focusMember.name}</h3>
              <div className="flex items-center justify-center gap-2 text-xs text-slate-500 mt-1">
                 {focusMember.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {focusMember.location}</span>}
              </div>
           </div>
           
           {/* Reset Button (If deep in tree) */}
           {focusId !== currentUser.id && (
             <button 
               onClick={() => setFocusId(currentUser.id)}
               className="mt-2 px-4 py-1 bg-indigo-100 text-indigo-700 text-xs font-bold rounded-full hover:bg-indigo-200 transition-colors"
             >
               Back to Me
             </button>
           )}
        </div>

        {/* 2. THE PLANETS (Connections) */}
        {connections.map((item, index) => {
          // Calculate circular position
          const total = connections.length;
          const angle = (index / total) * 2 * Math.PI - (Math.PI / 2); // Start from top
          const radius = 160; // Distance from center (px)
          const x = Math.cos(angle) * radius;
          const y = Math.sin(angle) * radius;

          return (
            <button
              key={item.member.id}
              onClick={() => setFocusId(item.member.id)}
              className="absolute w-16 h-16 rounded-full border-2 border-white shadow-lg transition-all hover:scale-110 hover:z-30 group bg-white"
              style={{
                transform: `translate(${x}px, ${y}px)`,
              }}
            >
              <img 
                src={item.member.avatarUrl || `https://ui-avatars.com/api/?name=${item.member.name}`} 
                className="w-full h-full rounded-full object-cover"
              />
              
              {/* Relationship Badge */}
              <div className={`absolute -top-2 -right-2 w-6 h-6 ${item.color} rounded-full flex items-center justify-center text-white border-2 border-white shadow-sm`}>
                <item.icon className="w-3 h-3" />
              </div>

              {/* Hover Name Tag */}
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-slate-800 text-white text-[10px] px-2 py-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-40">
                {item.member.name}
                <span className="block text-slate-400 font-bold">{item.relation}</span>
              </div>
            </button>
          );
        })}

          {/*
           NOTE: The Circles (NetworkView) page will display connections here once the user adds family relations (parents, children, spouse, siblings, etc.).
          */}
          {connections.length === 0 && (
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 translate-y-24 text-center w-64 pointer-events-none">
              <p className="text-sm text-slate-400 font-medium">
               No direct connections yet.<br />
               <span className="text-xs text-slate-500">
                As you add family members and build your tree, your connections will appear here!
               </span>
              </p>
            </div>
          )}

      </div>
      
      {/* Legend */}
      <div className="absolute bottom-6 flex gap-3 bg-white px-4 py-2 rounded-full border border-slate-200 shadow-sm z-10 overflow-x-auto max-w-[95vw] no-scrollbar text-[9px]">
         {/* Ascending generations */}
         <div className="flex items-center gap-1 font-bold text-slate-600 whitespace-nowrap"><div className="w-2 h-2 rounded-full bg-slate-500"></div> Great-GP</div>
         <div className="flex items-center gap-1 font-bold text-slate-600 whitespace-nowrap"><div className="w-2 h-2 rounded-full bg-violet-500"></div> Grandparent</div>
         <div className="flex items-center gap-1 font-bold text-slate-600 whitespace-nowrap"><div className="w-2 h-2 rounded-full bg-indigo-500"></div> Parent</div>
         <div className="flex items-center gap-1 font-bold text-slate-600 whitespace-nowrap"><div className="w-2 h-2 rounded-full bg-indigo-400"></div> Step-Parent</div>
         
         {/* Same generation */}
         <div className="flex items-center gap-1 font-bold text-slate-600 whitespace-nowrap"><div className="w-2 h-2 rounded-full bg-pink-500"></div> Spouse</div>
         <div className="flex items-center gap-1 font-bold text-slate-600 whitespace-nowrap"><div className="w-2 h-2 rounded-full bg-amber-500"></div> Sibling</div>
         <div className="flex items-center gap-1 font-bold text-slate-600 whitespace-nowrap"><div className="w-2 h-2 rounded-full bg-orange-500"></div> Aunt/Uncle</div>
         <div className="flex items-center gap-1 font-bold text-slate-600 whitespace-nowrap"><div className="w-2 h-2 rounded-full bg-cyan-500"></div> Cousin</div>
         
         {/* Descending generations */}
         <div className="flex items-center gap-1 font-bold text-slate-600 whitespace-nowrap"><div className="w-2 h-2 rounded-full bg-emerald-500"></div> Child</div>
         <div className="flex items-center gap-1 font-bold text-slate-600 whitespace-nowrap"><div className="w-2 h-2 rounded-full bg-rose-600"></div> Child In-Law</div>
         <div className="flex items-center gap-1 font-bold text-slate-600 whitespace-nowrap"><div className="w-2 h-2 rounded-full bg-emerald-600"></div> Grandchild</div>
         <div className="flex items-center gap-1 font-bold text-slate-600 whitespace-nowrap"><div className="w-2 h-2 rounded-full bg-emerald-700"></div> Great-GC</div>
         
         {/* In-laws */}
         <div className="flex items-center gap-1 font-bold text-slate-600 whitespace-nowrap"><div className="w-2 h-2 rounded-full bg-rose-500"></div> In-Law Parent</div>
         <div className="flex items-center gap-1 font-bold text-slate-600 whitespace-nowrap"><div className="w-2 h-2 rounded-full bg-rose-400"></div> In-Law Sibling</div>
      </div>

    </div>
  );
};

export default NetworkView;